import * as SwitchPrimitives from '@radix-ui/react-switch'
import { useEffect, useRef, useState } from 'react'
import { LuInfo } from 'react-icons/lu'
import { Link, useLocation } from 'react-router-dom'
import { usePrepareCart } from '@cannect/hooks/usePrepareCart'
import { isProgramOrder } from '@cannect/pages/WeightLossProgram/constants'
import { CART_LINK_CYCLE_PARAM } from '@cannect/services/resources/recurrency/constants'
import {
  formatApiDate,
  hasDocumentsPending,
  parseCartLinkCycle,
  pendingDocumentsLabel,
  reasonToAction,
  zipReasons
} from '@cannect/services/resources/recurrency/helpers'
import {
  useCartLinkRecurrency,
  useEligibility,
  useRecurrencyAccess
} from '@cannect/services/resources/recurrency/hooks'
import type { CartRecurrencySummary, EligibilityView } from '@cannect/services/resources/recurrency/types'
import { formatToBRL } from '@cannect/utils/formatters'
import { CycleSelector } from '../CycleSelector'
import { RecurrencyCartLinkBanner } from '../RecurrencyCartLinkBanner'
import { RecurrencyLoginPrompt } from '../RecurrencyLoginPrompt'
import { recurrencyCopy } from '../copy'

const HIDDEN_ITEM_CODES = ['PRODUCT_NOT_ENABLED', 'PRODUCT_NOT_FOUND']

const actionLink = (code: string, orderId: number, prescriberIsCannect?: boolean | null) => {
  const action = reasonToAction(code, { prescriberIsCannect })
  if (action === 'send_prescription')
    return { to: `/enviar-receita/${orderId}`, label: recurrencyCopy.cart.sendPrescription }
  if (action === 'schedule') return { to: '/agendamento', label: recurrencyCopy.cart.schedule }
  if (action === 'anvisa') return { to: '/conta?pagina=perfil-documentos', label: recurrencyCopy.cart.anvisa }
  return null
}

const shippingLabel = (summary: CartRecurrencySummary) => {
  if (summary.shippingMode === 'free') return recurrencyCopy.cart.shippingFree
  if (summary.shipping === null) return recurrencyCopy.cart.shippingQuoted
  return formatToBRL(summary.shipping)
}

const Row = ({ label, value }: { label: string; value: string }) => (
  <div className="flex justify-between gap-4 text-sm">
    <span className="text-gray-600">{label}</span>
    <span className="font-semibold text-gray-900">{value}</span>
  </div>
)

export type RecurrencyCartSectionProps = {
  // `cart_link` (010): carrinho do link do painel. A oferta vem do pedido
  // (`recurrencyOffer`), o switch pede login ou vínculo quando o pedido ainda não
  // é do usuário. O padrão mantém o comportamento do `/carrinho` (007/009).
  variant?: 'default' | 'cart_link'
}

// "Receber automaticamente" no resumo do passo 1 (um ciclo para o pedido
// todo). Visível só com o carrinho elegível ou com o plano já ligado; com
// itens habilitados mas inelegíveis, lista os motivos do serviço.
export const RecurrencyCartSection = ({ variant = 'default' }: RecurrencyCartSectionProps) => {
  const isCartLink = variant === 'cart_link'
  const {
    lastOrderCreated: order,
    recurrencyCycleDays,
    setRecurrencyCycleDays,
    updatingRecurrency,
    claimCartLink,
    cartLinkClaimStatus,
    cartLinkLoginRequired
  } = usePrepareCart()
  const { enabled } = useRecurrencyAccess()
  const cartLink = useCartLinkRecurrency()
  const { search } = useLocation()
  const [promptOpen, setPromptOpen] = useState(false)
  const autoPromptRef = useRef(false)
  const summary = order?.recurrencySummary ?? null
  const items: any[] = order?.items || []
  const isProgram = isProgramOrder(order)
  const probe = useEligibility(
    {
      productIds: items.map((item) => Number(item?.product_id ?? item?.product?.id)),
      quantities: items.map((item) => Number(item?.quantity) || 1),
      // 009: no checkout, documento faltando vira pendência (não bloqueia).
      origin: 'checkout'
    },
    // 010: no carrinho do link a oferta vem do pedido; sem sondagem.
    { enabled: !isCartLink && Boolean(order?.id) && !summary && !isProgram }
  )

  const cartId: string = new URLSearchParams(search).get('cart_id') || ''
  const offerOptions = cartLink.offer?.cycleOptions?.length ? cartLink.offer.cycleOptions : [60, 90, 120]
  const urlCycle = parseCartLinkCycle(new URLSearchParams(search).get(CART_LINK_CYCLE_PARAM), offerOptions)

  // 010: volta ao carrinho sem sessão depois de um pagamento ou PUT recusado
  // por falta de login (ou com o ciclo na URL): abre o aviso uma vez.
  useEffect(() => {
    if (!isCartLink || autoPromptRef.current || cartLink.mode !== 'login_required') return
    if (!cartLinkLoginRequired && !urlCycle) return
    autoPromptRef.current = true
    setPromptOpen(true)
  }, [isCartLink, cartLink.mode, cartLinkLoginRequired, urlCycle])

  if (isCartLink) {
    if (!order?.id || isProgram || cartLink.mode === 'hidden') return null
    if (cartLink.mode === 'other_patient') return <RecurrencyCartLinkBanner cartId={cartId} />
    if (cartLink.mode === 'blocked') {
      return (
        <section aria-label={recurrencyCopy.cart.toggle} className="rounded-lg bg-gray-50 p-4 text-sm text-gray-700">
          {cartLink.blockedReason === 'SELLER_CONDITIONS'
            ? recurrencyCopy.cartLink.sellerConditions
            : recurrencyCopy.cartLink.mixedItems}
        </section>
      )
    }
  } else if (!enabled || !order?.id || isProgram) {
    return null
  }

  const view: EligibilityView | null | undefined =
    order?.recurrencyEligibility ?? (isCartLink || probe.isError ? null : probe.data)

  if (!isCartLink && !summary) {
    if (!view || (view.reasons || []).includes('NOT_IN_PILOT')) return null
    const hasEnabledItem = view.items.some((item) => !item.reasons.some((code) => HIDDEN_ITEM_CODES.includes(code)))
    if (!view.eligible && !hasEnabledItem) return null
  }

  const isOn = Boolean(summary) && (!isCartLink || cartLink.mode === 'owner')
  const suggested = isCartLink
    ? (summary?.suggestedCycleDays ?? cartLink.offer?.suggestedCycleDays ?? 90)
    : (summary?.suggestedCycleDays ?? view?.summary?.suggestedCycleDays ?? 90)
  const viewOptions = view?.summary?.cycleDaysOptions?.length ? view.summary.cycleDaysOptions : [60, 90, 120]
  const options = isCartLink ? offerOptions : viewOptions
  // No carrinho do link o switch aparece sempre que há oferta; os motivos
  // pessoais (receita, já assinante) vêm na resposta do PUT depois do vínculo.
  const showSwitch = isCartLink ? !(view && !view.eligible && !isOn) : isOn || Boolean(view?.eligible)
  const switchBusy = updatingRecurrency || (isCartLink && cartLinkClaimStatus === 'loading')

  const handleToggle = async (checked: boolean) => {
    if (!isCartLink) {
      setRecurrencyCycleDays(checked ? suggested : null)
      return
    }
    if (!checked) {
      await setRecurrencyCycleDays(null)
      return
    }
    if (cartLink.mode === 'login_required') {
      setPromptOpen(true)
      return
    }
    if (cartLink.mode === 'claim_pending') {
      const result = await claimCartLink()
      // O resultado de erro é tratado pelo efeito do NewCart (banner/toast).
      if (!result.ok) return
    }
    await setRecurrencyCycleDays(suggested)
  }
  // 009: assinatura possível com documentos depois do pagamento (só exibe o
  // que a API informa).
  const documentsPending = hasDocumentsPending(view)
  const describedBy = documentsPending
    ? 'recurrency-cart-description recurrency-cart-documents'
    : 'recurrency-cart-description'

  if (!showSwitch && view) {
    const blockedItems = view.items.filter(
      (item) => !item.eligible && !item.reasons.some((code) => HIDDEN_ITEM_CODES.includes(code))
    )
    const orderReasons = zipReasons(view)

    return (
      <section aria-labelledby="recurrency-cart-ineligible" className="rounded-lg bg-gray-50 p-4 text-sm">
        <h3 id="recurrency-cart-ineligible" className="mb-2 font-semibold text-gray-900">
          {recurrencyCopy.cart.ineligibleTitle}
        </h3>
        <ul className="flex list-none flex-col gap-2 p-0">
          {blockedItems.map((item) => {
            const product = items.find((i) => Number(i?.product_id ?? i?.product?.id) === Number(item.productId))
            const name = item.name || product?.product?.translated_name || product?.product?.name || 'Produto'
            return zipReasons(item).map((reason) => {
              const link = actionLink(reason.code, order.id, item.prescriberIsCannect)
              return (
                <li key={`${item.productId}-${reason.code}`} className="text-gray-700">
                  <strong>{name}</strong>: {reason.message}
                  {link && (
                    <>
                      {' '}
                      <Link to={link.to} className="font-semibold text-primary-700 underline">
                        {link.label}
                      </Link>
                    </>
                  )}
                </li>
              )
            })
          })}
          {orderReasons.map((reason) => {
            const link = actionLink(reason.code, order.id)
            return (
              <li key={reason.code} className="text-gray-700">
                {reason.message}
                {link && (
                  <>
                    {' '}
                    <Link to={link.to} className="font-semibold text-primary-700 underline">
                      {link.label}
                    </Link>
                  </>
                )}
              </li>
            )
          })}
        </ul>
      </section>
    )
  }

  return (
    <section
      aria-labelledby="recurrency-cart-title"
      className="flex flex-col gap-3 rounded-lg border border-solid border-gray-200 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col">
          <label
            id="recurrency-cart-title"
            htmlFor="recurrency-cart-switch"
            className="text-sm font-semibold text-gray-900">
            {recurrencyCopy.cart.toggle}
          </label>
          <span id="recurrency-cart-description" className="text-xs text-gray-600">
            {recurrencyCopy.cart.toggleDescription}
          </span>
          {isCartLink && !isOn && cartLink.offer && (
            <span className="text-xs font-semibold text-primary-700">
              {recurrencyCopy.cartLink.discountHint(cartLink.offer.subscriberDiscountPercent)}
            </span>
          )}
        </div>
        <SwitchPrimitives.Root
          id="recurrency-cart-switch"
          checked={isOn}
          disabled={switchBusy}
          aria-describedby={describedBy}
          onCheckedChange={handleToggle}
          className="inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-700 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary-700 data-[state=unchecked]:bg-gray-300">
          <SwitchPrimitives.Thumb className="pointer-events-none block h-5 w-5 rounded-full bg-white shadow-lg transition-transform data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0" />
        </SwitchPrimitives.Root>
      </div>

      {documentsPending && (
        <div
          id="recurrency-cart-documents"
          role="note"
          className="flex gap-2 rounded-lg border border-solid border-information-300 bg-white p-3 text-xs text-gray-800">
          <LuInfo aria-hidden className="mt-0.5 shrink-0 text-information-700" />
          <p className="m-0">{recurrencyCopy.cart.documentsPending(pendingDocumentsLabel(view?.pendingDocuments))}</p>
        </div>
      )}

      {isOn && summary && (
        <div className="flex flex-col gap-2">
          <CycleSelector
            label={recurrencyCopy.cart.cycleLabel}
            options={options}
            value={recurrencyCycleDays}
            suggested={summary.suggestedCycleDays}
            disabled={updatingRecurrency}
            onChange={(cycleDays) => setRecurrencyCycleDays(cycleDays)}
          />
          <Row
            label={`${recurrencyCopy.cart.subscriberDiscount} (${summary.subscriberDiscountPercent}%)`}
            value={`- ${formatToBRL(summary.subscriberDiscount)}`}
          />
          <Row label={recurrencyCopy.cart.shipping} value={shippingLabel(summary)} />
          {summary.nextChargeAt && (
            <Row label={recurrencyCopy.cart.nextCharge} value={formatApiDate(summary.nextChargeAt)} />
          )}
          <p className="text-xs text-gray-600">{recurrencyCopy.cart.delivery(summary.deliveryEstimateDays)}</p>
          <p className="text-xs text-gray-600">{recurrencyCopy.cart.flexibility()}</p>
        </div>
      )}

      {isCartLink && (
        <RecurrencyLoginPrompt
          open={promptOpen}
          cartId={cartId}
          cycleDays={urlCycle ?? suggested}
          onClose={() => setPromptOpen(false)}
        />
      )}
    </section>
  )
}
